/**
 * The visitor's cookie choice, shared by the marketing site and the app SPA.
 *
 * Three states, and the third one is where everything happens: accepted,
 * rejected, and unanswered. Only about one visitor in fifty ever answers, so
 * what we do with the unanswered majority decides whether we can measure our
 * own funnel at all. That question is geography rather than preference — the EU
 * requires a yes before anything loads, US law requires a way out instead — so
 * it is not decided here. The marketing head snippet decides it, from the
 * device's time zone, before anything loads (src/marketing/index.html). The
 * /collect page deliberately does not: it has no banner to notify anyone with,
 * so it only ever honors an Accept given elsewhere.
 *
 * What IS absolute here: a stored 'rejected' stops every non-necessary tracker
 * in every region, and so does a Global Privacy Control signal. Rejection also
 * reaches backwards — forgetAdCookies deletes the ad cookies already dropped,
 * and the server clears the copies stored on the person, because the
 * Conversions API can otherwise keep reporting someone long after their browser
 * is gone (see src/edge/routes/events-routes.js).
 *
 * The choice lives in localStorage — the consent record itself is necessary
 * site functionality, not a tracking cookie.
 *
 * The head snippets read STORAGE_KEY directly (string compare, no import) —
 * keep that key in sync with this module.
 */

export type CookieConsent = 'accepted' | 'rejected';

const STORAGE_KEY = 'ori_cookie_consent';

// Every ad platform's match keys a registration copies server-side (edge
// auth/cookie.js readAdsCookies): Meta's _fbp / _fbc, TikTok's _ttp and the
// click id (ttclid, our ori_ttclid copy), Google's _gcl_aw / _gcl_gb and our
// ori_gclick, Reddit's _rdt_uuid and our ori_rdt_cid. Each platform's server
// events match on them, and their presence is the consent the forwarders fail
// closed on — so deleting them is the deletion that actually stops the
// reporting. Meta's pair only until 2026-10-30, when a rejected browser kept
// the TikTok, Google and Reddit keys for the next registration to copy.
const AD_COOKIES = ['_fbp', '_fbc', '_ttp', 'ttclid', 'ori_ttclid', '_gcl_aw', '_gcl_gb', 'ori_gclick', '_rdt_uuid', 'ori_rdt_cid'];

export function getCookieConsent(): CookieConsent | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    // Storage disabled (private mode / blocked) — treat as no consent.
    return null;
  }
}

export function setCookieConsent(value: CookieConsent): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage disabled — the banner reappears next load, which is the
    // safe (no-consent) default. Nothing else to do.
  }
}

/**
 * Forget the choice, so the banner comes back and can be answered again.
 *
 * This is what the footer's "Cookie preferences" link needs: a decision the
 * visitor cannot revisit is not really an opt-out, and the state laws that let
 * us load pixels before an answer are the same ones that require the way out to
 * stay available afterwards.
 */
export function clearCookieConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage disabled — nothing was stored to begin with */
  }
}

/**
 * Delete the ad platforms' cookies from this browser.
 *
 * Called on Reject. Setting consent alone stops the NEXT page load from
 * starting the pixel; it does nothing about the cookies this page already
 * dropped, and those cookies are exactly what a later registration would copy
 * server-side and report conversions against for weeks. Expiring them on both
 * the current host and its parent domain covers the pixel's own scope
 * (oriscen.com sets _fbp on the apex, and we may be on www).
 */
export function forgetAdCookies(): void {
  const host = window.location.hostname;
  // '.oriscen.com' from 'www.oriscen.com'; an apex host yields nothing extra.
  const parts = host.split('.');
  const apex = parts.length > 2 ? `.${parts.slice(-2).join('.')}` : `.${host}`;
  for (const name of AD_COOKIES) {
    for (const domain of ['', apex]) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}
